import { z } from 'zod';

/**
 * `defer-mobile-favourites` — server-backed device + network favourites.
 *
 * Power users tend to re-pick the same device + orientation +
 * network preset (and increasingly, the same geolocation /
 * permissions) for every flow they author. The Phase 8 mobile
 * emulation picker now lets a user **save** the current modal
 * state as a named favourite under the active org. Favourites are
 * scoped per-user (the writer's `users.id` is recorded), but read
 * by the whole org so a team can share "Pixel 8 · landscape · 3G ·
 * SF" without ad-hoc snippets.
 *
 * The payload shape is intentionally a superset of the picker's
 * working state (everything except `enabled`) so the favourite can
 * round-trip: applying a favourite simply spreads it onto the live
 * `MobileEmulationConfig` and the user clicks Save.
 *
 * Server schema: `mobile_emulation_favourites` (migration 101).
 *
 * Endpoints:
 *   GET    /api/orgs/:orgId/mobile-emulation-favourites
 *   POST   /api/orgs/:orgId/mobile-emulation-favourites
 *   DELETE /api/orgs/:orgId/mobile-emulation-favourites/:id
 */

/**
 * Geolocation override the favourite carries. Mirrors the shape
 * the picker's "Advanced" section persists onto
 * `MobileEmulationConfig.geolocation`.
 */
export const MobileFavouriteGeolocationSchema = z
  .object({
    latitude: z.number().min(-90).max(90),
    longitude: z.number().min(-180).max(180),
    accuracy: z.number().nonnegative().max(10_000).optional(),
    label: z.string().min(1).max(80).optional(),
  })
  .strict();
export type MobileFavouriteGeolocation = z.infer<typeof MobileFavouriteGeolocationSchema>;

export const MobileFavouriteCustomNetworkSchema = z
  .object({
    downloadThroughput: z.number().nonnegative().max(10_000_000_000),
    uploadThroughput: z.number().nonnegative().max(10_000_000_000),
    latencyMs: z.number().nonnegative().max(60_000),
  })
  .strict();
export type MobileFavouriteCustomNetwork = z.infer<typeof MobileFavouriteCustomNetworkSchema>;

/**
 * Per-flow permissions / clipboard / CSP toggles that the picker
 * persists onto `MobileEmulationConfig`. The runner already
 * understands `geolocation` and `permissions` via
 * `BrowserLaunchOptions`; the picker passes these through unchanged
 * for `defer-mobile-csp`.
 */
export const MOBILE_PERMISSION_IDS = [
  'geolocation',
  'camera',
  'microphone',
  'notifications',
  'clipboard-read',
  'clipboard-write',
] as const;
export type MobilePermissionId = (typeof MOBILE_PERMISSION_IDS)[number];
export const MobilePermissionIdSchema = z.enum(MOBILE_PERMISSION_IDS);

export const CLIPBOARD_MODES = ['inherit', 'allow', 'deny'] as const;
export type ClipboardMode = (typeof CLIPBOARD_MODES)[number];
export const ClipboardModeSchema = z.enum(CLIPBOARD_MODES);

export const MobileFavouriteConfigSchema = z
  .object({
    /** Mobile devices registry id. Required — the favourite always pins one device. */
    deviceId: z.string().min(1).max(80),
    orientation: z.enum(['portrait', 'landscape']),
    networkPresetId: z.string().min(1).max(40),
    customNetwork: MobileFavouriteCustomNetworkSchema.optional(),
    geolocation: MobileFavouriteGeolocationSchema.optional(),
    /** Path to a static image / video clip the runner can pipe into `getUserMedia`. Empty / omitted = no override. */
    cameraImagePath: z.string().min(1).max(500).optional(),
    /** Playwright `grantPermissions` list. */
    permissions: z.array(MobilePermissionIdSchema).max(MOBILE_PERMISSION_IDS.length).optional(),
    clipboardMode: ClipboardModeSchema.optional(),
    /** Pass-through to `BrowserContext.newContext({ bypassCSP })`. */
    bypassCSP: z.boolean().optional(),
  })
  .strict();
export type MobileFavouriteConfig = z.infer<typeof MobileFavouriteConfigSchema>;

export const MobileEmulationFavouriteSchema = z.object({
  id: z.string().uuid(),
  orgId: z.string().uuid(),
  userId: z.string().uuid(),
  label: z.string().min(1).max(80),
  config: MobileFavouriteConfigSchema,
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type MobileEmulationFavourite = z.infer<typeof MobileEmulationFavouriteSchema>;

export const CreateMobileEmulationFavouriteRequestSchema = z
  .object({
    label: z.string().min(1).max(80),
    config: MobileFavouriteConfigSchema,
  })
  .strict();
export type CreateMobileEmulationFavouriteRequest = z.infer<typeof CreateMobileEmulationFavouriteRequestSchema>;

export const ListMobileEmulationFavouritesResponseSchema = z.object({
  favourites: z.array(MobileEmulationFavouriteSchema),
});
export type ListMobileEmulationFavouritesResponse = z.infer<typeof ListMobileEmulationFavouritesResponseSchema>;

/**
 * Hard cap on rows per org. Keeps the UI manageable and stops
 * unbounded JSONB growth. Picker enforces the same number client-side.
 */
export const MOBILE_EMULATION_FAVOURITES_PER_ORG_LIMIT = 50;
